/**
 * isiXhosa: Common words
 *
 * What this file covers: Buttons, statuses, table controls, short notices, countries, phone numbers, password
 * rules and contact attributes used on many pages.
 *
 * Tone: The shortest everyday words. Buttons are verbs (Save, Cancel, Remove). Use one word per idea and never
 * vary it, because these words sit beside every other file's text.
 *
 * isiXhosa: Everyday isiXhosa as used in South African business. Keys not translated yet fall back to English,
 * so a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const common: Catalog = {
  'action.signOut': 'Phuma',
  'action.save': 'Gcina',
  'action.cancel': 'Rhoxisa',
  'action.create': 'Yenza',
  'action.delete': 'Cima',
  'action.search': 'Khangela',
  'common.loading': 'Iyalayisha…',
  'common.language': 'Ulwimi',
  'common.unknownError': 'Impazamo engaziwayo',
  'common.name': 'Igama',
  'common.email': 'I-imeyile',
  'common.status': 'Isimo',
  'common.type': 'Uhlobo',
  'common.description': 'Inkcazo',
  'common.created': 'Yenziwe',
  'common.updated': 'Ihlaziyiwe',
  'common.actions': 'Izenzo',
  'common.yes': 'Ewe',
  'common.no': 'Hayi',
  'common.all': 'Konke',
  'common.none': 'Akukho',
  'common.search': 'Khangela',
  'common.searchPlaceholder': 'Khangela…',
  'common.noResults': 'Akukho ziphumo.',
  'common.date': 'Umhla',
  'common.amount': 'Isixa',
  'common.total': 'Iyonke',
  'common.details': 'Iinkcukacha',
  'common.overview': 'Imbonakalo jikelele',
  'common.optional': 'Ngokuzikhethela',
  'common.required': 'Iyafuneka',
  'common.copied': 'Ikhutshelwe kwibhodi yokukhuphela.',
  'common.enabled': 'Ivunyelwe',
  'common.disabled': 'Ivaliwe',
  'common.never': 'Akhange',
  'common.unknown': 'Ayaziwa',
  'common.select': 'Khetha…',
  'common.you': 'Wena',
  'common.notAvailable': 'ayifumaneki',
  'action.edit': 'Hlela',
  'action.close': 'Vala',
  'action.refresh': 'Hlaziya',
  'action.retry': 'Zama kwakhona',
  'action.back': 'Emva',
  'action.apply': 'Sebenzisa',
  'action.clear': 'Sula',
  'action.copy': 'Khuphela',
  'action.view': 'Jonga',
  'action.confirm': 'Qinisekisa',
  'action.add': 'Yongeza',
  'action.remove': 'Susa',
  'action.submit': 'Ngenisa',
  'action.send': 'Thumela',
  'action.continue': 'Qhubeka',
  'action.next': 'Okulandelayo',
  'action.previous': 'Okwangaphambili',
  'action.download': 'Khuphela',
  'action.saveChanges': 'Gcina utshintsho',
  'action.discard': 'Lahla',
  'action.select': 'Khetha',
  'action.open': 'Vula',
  'action.enable': 'Vumela',
  'action.disable': 'Vala',
  'action.activate': 'Yenza isebenze',
  'action.pause': 'Nqumama',
  'action.start': 'Qalisa',
  'action.signIn': 'Ngena',
  'action.signUp': 'Bhalisa',
  'action.export': 'Thumela ngaphandle',
  'action.import': 'Ngenisa',
  'action.revoke': 'Rhoxisa',
  'action.regenerate': 'Yenza kwakhona',
  'action.keep': 'Gcina',
  'status.active': 'Iyasebenza',
  'status.inactive': 'Ayisebenzi',
  'status.pending': 'Ilindile',
  'status.approved': 'Iphunyeziwe',
  'status.rejected': 'Yaliwe',
  'status.failed': 'Isilele',
  'status.sent': 'Ithunyelwe',
  'status.delivered': 'Ihanjisiwe',
  'status.read': 'Ifundiwe',
  'status.draft': 'Uyilo',
  'status.submitted': 'Ingenisiwe',
  'status.paused': 'Inqunyanyisiwe',
  'status.running': 'Iyasebenza',
  'status.completed': 'Igqityiwe',
  'status.cancelled': 'Irhoxisiwe',
  'status.queued': 'Isemgceni',
  'status.scheduled': 'Icwangcisiwe',
  'status.expired': 'Iphelelwe lixesha',
  'status.revoked': 'Irhoxisiwe',
  'status.verified': 'Iqinisekisiwe',
  'status.unverified': 'Ayiqinisekiswanga',
  'status.open': 'Ivuliwe',
  'status.closed': 'Ivaliwe',
  'status.resolved': 'Isonjululwe',
  'status.archived': 'Igciniwe',
  'status.unknown': 'Ayaziwa',
  'toast.saved': 'Igciniwe',
  'toast.deleted': 'Icimiwe',
  'toast.copied': 'Ikhutshelwe',
  'toast.validation': 'Uqinisekiso',
  'toast.saveFailed': 'Ukugcina kusilele',
  'toast.deleteFailed': 'Ukucima kusilele',
  'toast.loadFailed': 'Ayikwazanga ukulayisha',
  'notFound.title': 'Iphepha alifunyanwanga',
  'notFound.backToDashboard': 'Buyela kwideshibhodi',
  'country.za': 'UMzantsi Afrika',
  'country.ng': 'INigeria',
  'country.ke': 'IKenya',
  'action.undo': 'Buyisela',
  'action.dismiss': 'Cima',
  'action.loadMore': 'Layisha okungakumbi',
  'attributes.group.contact': 'Umntu oqhagamshelwayo',
  'attributes.group.fields': 'Amasimi akho',
  'attributes.group.flow': 'Iinguqu zokuhamba',
  'attributes.contact.name': 'Igama',
  'attributes.contact.email': 'I-imeyile',
  'attributes.contact.msisdn': 'Inombolo yefowuni',
  'attributes.contact.locale': 'Ulwimi',
  'attributes.fallbackHint': 'Yongeza u-| nexabiso lokubuyela emva kwegama, kuye nabani na ongenanto apho: contact.name|apho',
  'attributes.flowFallbackHint': 'Yongeza u-?? nexabiso lokubuyela emva kwegama, xa kungekho nto: contact.name ?? apho'
};

export default common;
